import React, { useMemo, useEffect } from 'react';
import { Table, Tag, EllipsisSpan, usePaging, Paging, Button, Empty } from '@maxtropy/components';
import { useRequest } from 'ahooks';
import { Spin } from 'antd';
import dayjs from 'dayjs';
import { keepTwoOrLine } from '@/utils/utils';
import { getReportTimeRange, ReportType } from '../../const';
import {
  apiV2PipelinePressureMonitoringPipelinePressureMonitoringPagePost,
  apiV2ReportDataGasOverviewPost,
  apiV2ReportDataGasAlarmPost,
} from '@maxtropy/device-customer-apis-v2';
import CardBox from '../CardBox';
import iconAlarmCount from '../../imgs/icon_alarm_count.png';
import iconValveLeak from '../../imgs/icon_valve_leak.png';
import styles from './index.module.scss';
import type { Dayjs } from 'dayjs';

interface GasAnalysisProps {
  reportType: ReportType;
  selectedDate: Dayjs;
}

// 压力测试状态配置（generateStatus: 1-待生成, 2-已生成）
const PRESSURE_TEST_STATUS_MAP: Record<number, { color: string; text: string }> = {
  1: { color: 'default', text: '待生成' },
  2: { color: 'success', text: '已完成' },
};

const GasAnalysis: React.FC<GasAnalysisProps> = ({ reportType, selectedDate }) => {
  // 分页配置
  const pagingInfo = usePaging(50);
  const { pageOffset, pageSize, setTotalCount } = pagingInfo;

  // 计算时间范围
  const timeRange = useMemo(() => getReportTimeRange(reportType, selectedDate), [reportType, selectedDate]);

  // 统计粒度
  const timeResolution = useMemo(() => (reportType === ReportType.DAILY ? 'DAY' : 'MONTH'), [reportType]);

  // 用气分析总览接口
  const { data: overviewData, loading: overviewLoading } = useRequest(
    async () => {
      const res = await apiV2ReportDataGasOverviewPost({
        fromTime: timeRange.fromTime,
        toTime: timeRange.toTime,
        timeResolution,
      });
      return res;
    },
    {
      refreshDeps: [timeRange, timeResolution],
    }
  );

  // 分管泄漏阀门接口
  const { data: gasAlarmData, loading: gasAlarmLoading } = useRequest(
    async () => {
      const res = await apiV2ReportDataGasAlarmPost({
        fromTime: timeRange.fromTime,
        toTime: timeRange.toTime,
        timeResolution,
      });
      return res;
    },
    {
      refreshDeps: [timeRange, timeResolution],
    }
  );

  // 管道压力测试分布接口
  const { data: pressureTestRes, loading: pressureTestLoading } = useRequest(
    async () => {
      const res = await apiV2PipelinePressureMonitoringPipelinePressureMonitoringPagePost({
        startTime: timeRange.fromTime,
        endTime: timeRange.toTime,
        page: pageOffset,
        size: pageSize,
      });
      return res;
    },
    {
      refreshDeps: [timeRange, pageOffset, pageSize],
    }
  );

  // 更新总数
  useEffect(() => {
    setTotalCount(pressureTestRes?.total ?? 0);
  }, [pressureTestRes?.total, setTotalCount]);

  // 压力测试表格列配置
  const pressureTestColumns = useMemo(
    () => [
      {
        title: '压力测试名称',
        dataIndex: 'taskName',
        width: 150,
        render: (v: string, record: { id?: number }) => (
          <Button
            type="link"
            onClick={() => window.open(`/pipeline/pressure/monitoring/detail/${record.id}`, '_blank')}
          >
            <EllipsisSpan value={v ?? '--'} />
          </Button>
        ),
      },
      {
        title: '测试区域',
        dataIndex: 'areaInfo',
        width: 100,
        render: (areas: { zoneName?: string }[]) => {
          const areaNames = areas
            ?.map(a => a.zoneName)
            .filter(Boolean)
            .join(', ');
          return areaNames || '--';
        },
      },
      {
        title: '压降幅度',
        dataIndex: 'pressureDropMagnitude',
        width: 100,
        render: (v: number) => (v != null ? `${keepTwoOrLine(v)}%` : '--'),
      },
      {
        title: '测试周期',
        dataIndex: 'period',
        width: 180,
        render: (_: unknown, record: { startTime?: string; endTime?: string }) => {
          const start = record.startTime ? dayjs(record.startTime).format('YYYY-MM-DD HH:mm:ss') : '--';
          const end = record.endTime ? dayjs(record.endTime).format('YYYY-MM-DD HH:mm:ss') : '--';
          const period = record.startTime && record.endTime ? `${start} ~ ${end}` : '--';
          return <EllipsisSpan value={period} />;
        },
      },
      {
        title: '状态',
        dataIndex: 'generateStatus',
        width: 80,
        render: (v: number) => {
          const config = PRESSURE_TEST_STATUS_MAP[v] || { color: 'default', text: '--' };
          return (
            <Tag border="solid" color={config.color}>
              {config.text}
            </Tag>
          );
        },
      },
      {
        title: '操作时间',
        dataIndex: 'operationTime',
        width: 150,
        render: (v: string) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '--'),
      },
      {
        title: '操作人',
        dataIndex: 'operator',
        width: 80,
        render: (v: string) => v || '--',
      },
    ],
    []
  );

  return (
    <CardBox title="用气分析">
      {/* 第一行：总览 + 分管阀门泄漏 */}
      <div className={styles.chart_row}>
        {/* 用气监测总览 */}
        <div className={styles.overview_section}>
          <div className={styles.section_title}>总览</div>
          {/* 2*2 排列 */}
          <Spin spinning={overviewLoading}>
            <div className={styles.overview_grid}>
              {/* 第一行：报警总数 + 阀门泄露 */}
              <div className={styles.overview_item_warning}>
                <div className={styles.overview_icon_wrapper}>
                  <img className={styles.overview_icon} src={iconAlarmCount} alt="" />
                </div>
                <div className={styles.overview_content}>
                  <div className={styles.overview_label}>报警总数</div>
                  <div className={styles.overview_value}>{overviewData?.gasHighLevelAlarm ?? '--'}</div>
                </div>
              </div>
              <div className={styles.overview_item_warning}>
                <div className={styles.overview_icon_wrapper}>
                  <img className={styles.overview_icon} src={iconValveLeak} alt="" />
                </div>
                <div className={styles.overview_content}>
                  <div className={styles.overview_label}>阀门泄露</div>
                  <div className={styles.overview_value}>{overviewData?.branchValveLeakage ?? '--'}</div>
                </div>
              </div>
              {/* 第二行：压力测试次数 + 压力最大总体下降幅度 */}
              <div className={styles.overview_item}>
                <div className={styles.overview_label}>压力测试次数</div>
                <div className={styles.overview_value}>{overviewData?.pressureCount ?? '--'}</div>
              </div>
              <div className={styles.overview_item}>
                <div className={styles.overview_label}>压力最大总体下降幅度</div>
                <div className={styles.overview_value}>{keepTwoOrLine(overviewData?.percentage)}%</div>
              </div>
            </div>
          </Spin>
        </div>

        {/* 分管泄漏阀门表格 */}
        <div className={styles.chart_col}>
          <Table
            loading={gasAlarmLoading}
            columns={[
              {
                title: '分管泄漏阀门',
                dataIndex: 'deviceName',
                render: (v: string) => <EllipsisSpan value={v} />,
              },
              {
                title: '监测时间',
                dataIndex: 'ts',
                render: (v: number) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '--'),
              },
            ]}
            dataSource={gasAlarmData?.list ?? []}
            rowKey={record => `${record.deviceId}${record.ts}`}
            pagination={false}
            scroll={{ y: 200 }}
            locale={{ emptyText: <Empty description="无阀门泄露报警" /> }}
          />
        </div>
      </div>

      {/* 第二行：管道压力测试分布 - 占整个一行 */}
      <div className={styles.pressure_test_row}>
        <div className={styles.section_title}>管道压力测试</div>
        <Table
          columns={pressureTestColumns}
          dataSource={pressureTestRes?.list ?? []}
          rowKey="id"
          loading={pressureTestLoading}
          pagination={false}
          scroll={{ x: 900 }}
          locale={{ emptyText: <Empty description="未进行压力测试" /> }}
        />
        <Paging pagingInfo={pagingInfo} />
      </div>
    </CardBox>
  );
};

export default GasAnalysis;
